import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { calculateOfferings } from "@redotech/redo-model/extended-warranties/calculate-offerings";
import {
  CART_ATTRIBUTE_KEY,
  Collection,
  ExtendedWarrantyDefaultSelection,
  getDefaultSelectedOffering,
  Offering,
  OfferingSelection,
  Selections,
  TrackedVariant,
} from "@redotech/redo-model/extended-warranty";
import { useEffect, useMemo, useState } from "react";
import { renderCart } from "../../render-cart";
import { DEFAULT_TOGGLE_CLICK_SELECTOR } from "../../selectors";
import { ExtensionShopifyClientContext } from "../../shopify";
import { useCart } from "../../utils/cart";
import { EwSettingsContext } from "../provider/ew-settings-provider";
import { useClickRefresh } from "./use-click-refresh";

type ExpandedWarranties = {
  trackedVariantOfferings: Record<string, Record<string, Offering>>;
  trackedVariantFlatOfferings: Record<string, Offering[]>;
  trackedVariantSelectedOffering: Record<string, Offering>;
  trackedVariantCartIndex: Record<string, number | undefined>;
  updateSelectedOffering: (variantId: string, offering: Offering) => void;
};

export function useExpandedWarranties({
  trackedVariants,
}: {
  trackedVariants: TrackedVariant[];
}): ExpandedWarranties {
  const { collectionSettings } = useRequiredContext(EwSettingsContext);
  const shopifyClient = useRequiredContext(ExtensionShopifyClientContext);

  const clickEvent = useClickRefresh({
    selector: DEFAULT_TOGGLE_CLICK_SELECTOR,
  });

  const cart = useCart(clickEvent);

  const [trackedVariantCollections, setTrackedVariantCollections] = useState<
    Record<string, Collection[]>
  >({});
  const [cartSelections, setCartSelections] = useState<Selections | undefined>(
    undefined,
  );

  const trackedVariantOfferings = useMemo(() => {
    if (!collectionSettings) {
      return {};
    }

    return trackedVariants.reduce<
      Record<string, Record<string, Offering>> | undefined
    >((acc, variant) => {
      if (acc === undefined) return undefined;

      const variantCollections = trackedVariantCollections?.[variant.variantId];
      if (!variantCollections) {
        return undefined;
      }

      const offerings = calculateOfferings({
        collectionOfferings: collectionSettings,
        variant: {
          variantId: variant.variantId,
          price: variant.price,
          collections: variantCollections,
        },
        // TODO: Figure out how to set offering label
      });
      if (!offerings) {
        return undefined;
      }

      acc[variant.variantId] = Object.fromEntries(
        offerings.map((offering) => [offering.id, offering]),
      );
      return acc;
    }, {});
  }, [trackedVariants, collectionSettings, trackedVariantCollections]);

  const trackedVariantSelectedOffering = useMemo(() => {
    if (!trackedVariantOfferings) return undefined;
    if (!cartSelections) return undefined;

    return Object.entries(trackedVariantOfferings).reduce(
      (acc, [variantId, offerings]) => {
        const selection = cartSelections?.[variantId];
        const selectedOffering = selection
          ? offerings[selection.timeId]
          : getDefaultSelectedOffering(
              offerings,
              ExtendedWarrantyDefaultSelection.NO_COVERAGE,
            );

        if (selectedOffering) {
          acc[variantId] = selectedOffering;
        }
        return acc;
      },
      {} as Record<string, Offering>,
    );
  }, [trackedVariantOfferings, cartSelections]);

  const trackedVariantFlatOfferings = useMemo(() => {
    if (!trackedVariantOfferings) return {};

    return Object.entries(trackedVariantOfferings).reduce(
      (acc, [variantId, offerings]) => {
        acc[variantId] = Object.values(offerings);
        return acc;
      },
      {} as Record<string, Offering[]>,
    );
  }, [trackedVariantOfferings]);

  const trackedVariantCartIndex = useMemo(() => {
    if (!cart) return {};

    return Object.entries(cart.items).reduce(
      (acc, [index, item]) => {
        acc[item.variant_id.toString()] = Number(index);
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [cart]);

  const updateTrackedVariantCollections = useHandler(
    async (newVariants: TrackedVariant[]) => {
      if (newVariants.length === 0) return;

      const newEntriesPromises = newVariants.map(async (variant) => [
        variant.variantId,
        (await shopifyClient.getCollections(variant.productId)) ?? [],
      ]);
      const newEntries = await Promise.all(newEntriesPromises);
      setTrackedVariantCollections((prev) => ({
        ...prev,
        ...Object.fromEntries(newEntries),
      }));
    },
  );

  const updateCartSelections = useHandler(
    async (cartSelections: Selections | undefined) => {
      await shopifyClient.updateCartAttribute({
        attributes: [
          {
            key: CART_ATTRIBUTE_KEY,
            value: cartSelections ? JSON.stringify(cartSelections) : "{}",
          },
        ],
      });
      await renderCart();
    },
  );

  const updateSelectedOffering = useHandler(
    (variantId: string, offering: Offering) => {
      const lineItem = cart?.items.find(
        (item) => item.variant_id.toString() === variantId,
      );
      const newSelection: OfferingSelection = {
        collectionHandle: offering.collectionHandle,
        timeId: offering.id,
        externalDisplayPriceSet: {
          currency: offering.price.currency,
          amount: offering.price.amount,
          quantity: lineItem?.quantity ?? 1,
        },
      };
      const updatedSelections = {
        ...cartSelections,
        [variantId]: newSelection,
      };

      void updateCartSelections(updatedSelections);
      setCartSelections(updatedSelections);
    },
  );

  useEffect(() => {
    const newVariants = trackedVariants.filter(
      (variant) => !trackedVariantCollections?.[variant.variantId],
    );
    void updateTrackedVariantCollections(newVariants);
  }, [
    trackedVariants,
    trackedVariantCollections,
    updateTrackedVariantCollections,
  ]);

  // Handle cart attribute initialization, parsing, and cleanup
  useEffect(() => {
    if (!cart) return;

    const cartSelections = cart.attributes?.[CART_ATTRIBUTE_KEY];

    // Initialize cart selection attribute if it doesn't exist
    if (!cartSelections) {
      void updateCartSelections(undefined);
      return;
    }

    try {
      const selections = JSON.parse(cartSelections) as Selections;

      // Remove selections for items no longer in cart
      // Update quantity for each selection
      const lineItems = cart.items;
      const newSelections: Selections = {};
      for (const [variantId, selection] of Object.entries(selections)) {
        const lineItem = lineItems.find(
          (item) => item.variant_id.toString() === variantId,
        );
        if (!lineItem) {
          continue;
        }
        let externalDisplayPriceSet = selection.externalDisplayPriceSet;
        if (externalDisplayPriceSet) {
          externalDisplayPriceSet = {
            ...externalDisplayPriceSet,
            quantity: lineItem.quantity,
          };
        }
        newSelections[variantId] = { ...selection, externalDisplayPriceSet };
      }

      setCartSelections(newSelections);
      void updateCartSelections(newSelections);
    } catch (error) {
      console.error(error);
    }
  }, [cart, updateCartSelections]);

  return {
    trackedVariantOfferings: trackedVariantOfferings ?? {},
    trackedVariantFlatOfferings: trackedVariantFlatOfferings ?? {},
    trackedVariantSelectedOffering: trackedVariantSelectedOffering ?? {},
    trackedVariantCartIndex: trackedVariantCartIndex ?? {},
    updateSelectedOffering,
  };
}
